import React, { useState } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { key: "delay", label: "⏳ Ritardo spedizione", subject: (o) => `Aggiornamento sul tuo ordine #${o}`, body: (items) => `ti scrivo per aggiornarti sul tuo ordine: la spedizione subirà un piccolo ritardo di qualche giorno rispetto al previsto${items ? ` (${items})` : ""}.\n\nStiamo facendo il possibile per partire al più presto e ti invieremo il numero di tracciamento appena il pacco sarà in viaggio. Ci scusiamo per l'attesa e ti ringraziamo per la pazienza.` },
  { key: "unavailable", label: "❌ Pezzo non disponibile", subject: (o) => `Ordine #${o}: un articolo non è più disponibile`, body: (items) => `purtroppo uno degli articoli del tuo ordine${items ? ` (${items})` : ""} non è più disponibile: l'ultimo pezzo è andato via prima che riuscissimo a evaderlo.\n\nPossiamo procedere in due modi: rimborsarti subito l'importo di quell'articolo, oppure sostituirlo con un altro numero/modello a tua scelta. Fammi sapere cosa preferisci rispondendo a questa email.` },
  { key: "address", label: "📍 Richiesta indirizzo", subject: (o) => `Ordine #${o}: conferma indirizzo di spedizione`, body: () => `prima di spedire il tuo ordine avrei bisogno di una conferma dell'indirizzo di consegna: l'indirizzo indicato sembra incompleto (manca ad esempio il numero civico, il CAP o un riferimento per il corriere).\n\nPuoi rispondere a questa email con l'indirizzo completo e un numero di telefono per il corriere? Appena ricevuto, spediamo subito.` },
  { key: "pickup", label: "🏪 Pronto per il ritiro", subject: (o) => `Ordine #${o} pronto per il ritiro in edicola`, body: (items) => `il tuo ordine${items ? ` (${items})` : ""} è pronto per il ritiro in edicola a Villaricca, Corso Europa 175.\n\nOrari: lunedì–sabato 9:00–19:00. Ti aspettiamo!` },
];

/** "Email cliente" button + inline composer: sends a free email to the order's customer. */
export const OrderEmailButton = ({ order }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(`Il tuo ordine #${String(order.id || "").slice(0, 8)} — Edicola Cerasuolo`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const email = order.customer_info?.email;
  const shortId = String(order.id || "").slice(0, 8);
  const itemsText = (order.items || []).map((it) => it.article?.title || it.title).filter(Boolean).slice(0, 3).join(", ");
  const applyPreset = (p) => { setSubject(p.subject(shortId)); setMessage(p.body(itemsText)); };

  const send = async () => {
    if (!subject.trim() || !message.trim()) return;
    setBusy(true);
    setResult("");
    try {
      const r = await axios.post(`${API}/orders/${order.id}/email`, { subject, message });
      setResult(`✅ Email inviata a ${r.data.to}`);
      setMessage("");
      setTimeout(() => { setOpen(false); setResult(""); }, 2500);
    } catch (e) {
      setResult(`❌ ${e.response?.data?.detail || "Invio fallito"}`);
    } finally {
      setBusy(false);
    }
  };

  if (!email) return null;
  return (
    <div className="mt-3" data-testid={`order-email-${order.id}`}>
      <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}
        className="border-pink-300 text-pink-700 hover:bg-pink-50" title={`Scrivi a ${email}`} data-testid={`order-email-toggle-${order.id}`}>
        ✉️ Email al cliente
      </Button>
      {open && (
        <div className="mt-2 border border-pink-200 bg-pink-50/40 rounded-lg p-3 space-y-2" data-testid={`order-email-form-${order.id}`}>
          <p className="text-xs text-gray-600">A: <strong>{email}</strong></p>
          <div className="flex flex-wrap gap-1.5" data-testid={`order-email-presets-${order.id}`}>
            {PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => applyPreset(p)} data-testid={`order-email-preset-${p.key}-${order.id}`}
                className="text-xs border border-gray-300 bg-white hover:bg-pink-100 hover:border-pink-300 rounded-full px-2.5 py-1 transition-colors">{p.label}</button>
            ))}
          </div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120}
            className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Oggetto" data-testid={`order-email-subject-${order.id}`} />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={3000}
            className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Scrivi il messaggio per il cliente…" data-testid={`order-email-message-${order.id}`} />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={send} disabled={busy || !subject.trim() || !message.trim()} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid={`order-email-send-${order.id}`}>
              {busy ? "Invio…" : "Invia email"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`order-email-cancel-${order.id}`}>Annulla</Button>
            {result && <span className="text-xs font-semibold" data-testid={`order-email-result-${order.id}`}>{result}</span>}
          </div>
          {order.admin_emails?.length > 0 && (
            <p className="text-[11px] text-gray-500">Ultima email inviata: {new Date(order.admin_emails[order.admin_emails.length - 1].sent_at).toLocaleString("it-IT")} — "{order.admin_emails[order.admin_emails.length - 1].subject}"</p>
          )}
        </div>
      )}
    </div>
  );
};
